export const DIVISION_META = {
  unitile: { name: "Unitile", tagline: "Raised Access Floors", primary: "#E63946", soft: "#FEE2E2", text: "#991B1B" },
  univicoustic: { name: "Univicoustic", tagline: "PET Acoustic Panels", primary: "#f04935", soft: "#FFEAE7", text: "#B91C1C" },
  uflex: { name: "U-Flex", tagline: "Structural Ceiling Systems", primary: "#F4A261", soft: "#FEF3C7", text: "#92400E" },
};

export function divisionMeta(id) {
  return DIVISION_META[id] || { name: "All Divisions", tagline: "United Group", primary: "#111827", soft: "#F3F4F6", text: "#111827" };
}

export const ROLE_LABELS = {
  super_admin: "Super Admin",
  management: "Management",
  sales_head: "National Sales Head",
  region_head: "Region Head",
  sales_manager: "Sales Manager",
  salesperson: "Salesperson",
  bd: "Business Development",
  sc: "Sales Coordinator",
  sales_coordinator: "Sales Coordinator",
  dealer: "Channel Partner",
  factory: "Factory",
  marketing: "Marketing",
};

export const ROLES = Object.keys(ROLE_LABELS);

export const REGIONS = ["North", "South", "West", "Exports", "OEM", "National"];

// Internal sales roles that operate within the hierarchy (region-scoped views).
export const SALES_ROLES = ["sales_head", "region_head", "sales_manager", "salesperson", "bd", "sc", "sales_coordinator"];

export const LEAD_STATUSES = ["New", "Contacted", "Nurturing", "Qualified", "Lead Lost"];
export const DEAL_STAGES = ["Quotation / BOQ", "Negotiation & Follow-up", "Order Won", "Order Booked", "Order Fulfilled", "Order Lost"];
export const QUOTE_STATUSES = ["draft", "pending_approval", "approved", "rejected", "sent", "accepted", "revised"];
export const PRODUCT_TYPES = ["HD", "VMT", "Both HD/VMT", "Ceiling Products"];

export const LEAD_STATUS_COLOR = {
  New: { bg: "#EEF2FF", text: "#3730A3", dot: "#6366F1" },
  Contacted: { bg: "#ECFEFF", text: "#155E75", dot: "#06B6D4" },
  Nurturing: { bg: "#FEF3C7", text: "#92400E", dot: "#F59E0B" },
  Qualified: { bg: "#D1FAE5", text: "#065F46", dot: "#10B981" },
  "Lead Lost": { bg: "#FEE2E2", text: "#991B1B", dot: "#EF4444" },
};

export const RATING_COLOR = {
  Hot: { bg: "#FEE2E2", text: "#991B1B", dot: "#EF4444" },
  Warm: { bg: "#FFEDD5", text: "#9A3412", dot: "#F97316" },
  Cold: { bg: "#DBEAFE", text: "#1E40AF", dot: "#3B82F6" },
  None: { bg: "#F3F4F6", text: "#6B7280", dot: "#9CA3AF" },
};

export const QUOTE_STATUS_COLOR = {
  draft: { bg: "#F3F4F6", text: "#374151" },
  pending_approval: { bg: "#FEF3C7", text: "#92400E" },
  approved: { bg: "#D1FAE5", text: "#065F46" },
  rejected: { bg: "#FEE2E2", text: "#991B1B" },
  sent: { bg: "#DBEAFE", text: "#1E40AF" },
  accepted: { bg: "#DCFCE7", text: "#166534" },
  revised: { bg: "#EDE9FE", text: "#5B21B6" },
};

export const SLA_STATE_COLOR = {
  on_track: { bg: "#FEF3C7", text: "#92400E", dot: "#F59E0B", label: "SLA pending" },
  breached: { bg: "#FEE2E2", text: "#991B1B", dot: "#EF4444", label: "SLA breached" },
  met: { bg: "#D1FAE5", text: "#065F46", dot: "#10B981", label: "SLA met" },
};

export const SAMPLE_STATUS_COLOR = {
  Requested: { bg: "#FEF3C7", text: "#92400E", dot: "#F59E0B" },
  Approved: { bg: "#DBEAFE", text: "#1E40AF", dot: "#3B82F6" },
  Dispatched: { bg: "#EDE9FE", text: "#5B21B6", dot: "#8B5CF6" },
  Delivered: { bg: "#D1FAE5", text: "#065F46", dot: "#10B981" },
  Rejected: { bg: "#FEE2E2", text: "#991B1B", dot: "#EF4444" },
};

export const SAMPLE_STATUSES = ["Requested", "Approved", "Dispatched", "Delivered", "Rejected"];

export const STAGE_INDEX = DEAL_STAGES.reduce((a, s, i) => ((a[s] = i), a), {});

export function formatINR(value) {
  const n = Number(value || 0);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

export function formatINRShort(value) {
  return (Number(value || 0) / 10000000).toFixed(2);
}

// Voice / AI transcription features (call recording, add lead/opportunity by voice) — disabled while API credits are low
export const VOICE_FEATURES_ENABLED = false;

export function formatINRCr(value) {
  return (Number(value || 0) / 10000000).toFixed(2);
}

export function formatINRTens(value) {
  return formatINRCr(value);
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
}

export function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return Math.floor(diff / 60) + "m ago";
  if (diff < 86400) return Math.floor(diff / 3600) + "h ago";
  if (diff < 604800) return Math.floor(diff / 86400) + "d ago";
  return formatDate(iso);
}

export function initials(name = "") {
  return name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

// Who may delete a given record (mirrors backend rules in leads/contacts/opportunities routers).
// Heads, admins AND sales managers may delete records within their division scope.
export function canDelete(user, entity = {}) {
  if (!user) return false;
  return ["super_admin", "sales_head", "sales_manager"].includes(user.role);
}

// Leads & opportunities can only be deleted by Super Admin and the National Sales Head.
export function canDeleteLeadDeal(user) {
  if (!user) return false;
  return ["super_admin", "sales_head"].includes(user.role);
}

// Hardcoded billing entity for the printable quotation (from the standard Proforma Invoice).
export const SELLER = {
  name: "United Acoustic Pvt Ltd",
  tagline: "UniVicoustic · PET Acoustic Panels · A United Group Company",
  address: "Survey No.271/4, New Sr No.3099/1, Malkhet, Near Gram Panchayat Malkhet, Khattalwada, Valsad - 396150, Gujarat, India",
  gstin: "24AADCU3918R1ZT",
  pan: "AADCU3918R",
  state: "Gujarat",
  stateCode: "24",
  phone: "+91 63598 63695",
  email: "sales@united-group.in",
  jurisdiction: "Subject to Mumbai Jurisdiction",
};
export const GST_RATE = 5; // % GST applied on the taxable value in the printable quote

// Role-based navigation
const ALL_SALES = ["super_admin", "sales_head", "region_head", "sales_manager", "salesperson", "bd", "sc", "sales_coordinator"];
const NON_MARKETING = ROLES.filter((r) => r !== "marketing");
const MGMT_BI = NON_MARKETING.filter((r) => r !== "dealer");
const SALES_NO_HEAD = ALL_SALES.filter((r) => r !== "sales_head");
const SALES_FIELD = SALES_NO_HEAD.filter((r) => r !== "region_head" && r !== "sales_manager");
export const NAV_GROUPS = [
  {
    key: "sales-bi", label: "Sales BI", icon: "Gauge",
    items: [
      { to: "/sales-bi", label: "Dashboard", icon: "LayoutDashboard", roles: ["super_admin", "management", "sales_head", "region_head", "sales_manager", "sales_coordinator", "salesperson", "bd", "sc"] },
      { to: "/my-team", label: "Business Insights", icon: "UsersRound", roles: ["super_admin", "management", "sales_head", "region_head", "sales_manager", "salesperson"] },
    ],
  },
  {
    key: "sales-crm", label: "Sales CRM", icon: "Users2",
    items: [
      { to: "/universe", label: "Universe", icon: "Database", roles: ["super_admin", "management", "sales_head", "region_head", "sales_manager", "sales_coordinator", "salesperson", "bd", "sc", "marketing"] },
      { to: "/leads", label: "Leads", icon: "Users2", roles: [...ALL_SALES.filter((r) => r !== "dealer"), "marketing"] },
      { to: "/lead-desk", label: "Lead Desk", icon: "ScanLine", roles: [...ALL_SALES.filter((r) => r !== "dealer"), "marketing"] },
      { to: "/deals", label: "Opportunities", icon: "TrendingUp", roles: [...ALL_SALES.filter((r) => r !== "dealer"), "management", "factory", "marketing"] },
      { to: "/contacts", label: "Contacts", icon: "Contact", roles: [...ALL_SALES.filter((r) => r !== "dealer"), "marketing"] },
    ],
  },
  {
    key: "bd-bi", label: "BD BI", icon: "Activity",
    items: [
      { to: "/bd", label: "Dashboard", icon: "LayoutDashboard", roles: ["bd", "sales_head", "super_admin"] },
    ],
  },
  {
    key: "dealer-bi", label: "Dealer BI", icon: "BarChart3",
    items: [
      { to: "/dealer-bi", label: "Dashboard", icon: "LayoutDashboard", roles: ["super_admin", "management", "sales_head", "region_head", "sales_manager", "sales_coordinator", "salesperson"] },
      { to: "/dealer-insights", label: "Business Insights", icon: "BarChart3", roles: ["super_admin", "management", "sales_head", "region_head", "sales_manager", "sales_coordinator", "salesperson"] },
    ],
  },
  {
    key: "dealer-crm", label: "Dealer CRM", icon: "Building2",
    items: [
      { to: "/dealer-crm/universe", label: "Universe", icon: "Database", roles: [...ALL_SALES, "management", "dealer"] },
      { to: "/dealer-crm/leads", label: "Leads", icon: "Users2", roles: [...ALL_SALES, "dealer"] },
      { to: "/dealer-crm/deals", label: "Opportunities", icon: "TrendingUp", roles: [...ALL_SALES, "management", "dealer"] },
      { to: "/dealer-crm/contacts", label: "Contacts", icon: "Contact", roles: [...ALL_SALES, "dealer"] },
    ],
  },
  {
    key: "utility", label: "Utility", icon: "Wrench",
    items: [
      { to: "/daily-calls", label: "Daily Calls", icon: "PhoneCall", roles: ALL_SALES },
      { to: "/quotes", label: "Quotes", icon: "FileText", roles: [...SALES_FIELD, "dealer"] },
      { to: "/samples", label: "Samples", icon: "Package2", roles: [...SALES_FIELD, "dealer", "factory"] },
      { to: "/products", label: "Product Catalog", icon: "Package", roles: ["super_admin", "sales_coordinator", "salesperson", "bd", "sc"] },
      { to: "/dealers", label: "Dealers", icon: "Building2", roles: ["super_admin", "sales_head", "salesperson", "bd", "sc", "sales_coordinator", "management"] },
      { to: "/dealer-assignment", label: "Dealer Assignment", icon: "Network", roles: ["super_admin"] },
      { to: "/marketing/dashboard", label: "Marketing Dashboard", icon: "Megaphone", roles: ["marketing", "super_admin"] },
      { to: "/marketing/campaigns", label: "Campaigns", icon: "Send", roles: ["marketing", "super_admin"] },
      { to: "/marketing/brevo", label: "Brevo Hub", icon: "CloudCog", roles: ["marketing", "super_admin"] },
      { to: "/marketing/templates", label: "Email Templates", icon: "Mail", roles: ["marketing", "super_admin"] },
      { to: "/erp-sync-log", label: "ERP Sync Log", icon: "Activity", roles: ["super_admin"] },
      { to: "/sales-bi-data", label: "Sales BI Data", icon: "Database", roles: ["super_admin"] },
      { to: "/milestone-pdf", label: "Milestone PDF", icon: "FileText", roles: ["super_admin"] },
      { to: "/universe-cleanup", label: "Universe Cleanup", icon: "Database", roles: [...SALES_FIELD, "dealer"] },
      { to: "/targets", label: "Targets", icon: "Target", roles: ["super_admin"] },
      { to: "/users", label: "Users", icon: "ShieldCheck", roles: ["super_admin", "sales_head", "management"] },
      { to: "/team-activity", label: "Team Activity", icon: "Activity", roles: ["super_admin", "region_head", "sales_manager"] },
      { to: "/checkin-map", label: "Check-in Map", icon: "MapPin", roles: ["super_admin", "sales_head", "management", "region_head", "sales_manager"] },
      { to: "/so-uploads", label: "Milestone Uploads", icon: "CloudCog", roles: ["super_admin", "sales_head", "management"] },
      { to: "/catalogue-library", label: "Catalogue Library", icon: "BookOpen", roles: ["super_admin", "sales_head", "management", "marketing"] },
      { to: "/settings", label: "Settings", icon: "Settings", roles: NON_MARKETING },
    ],
  },
];

export const NAV_ITEMS = NAV_GROUPS.flatMap((g) => g.items);
